import React from "react";
import { Chart } from "react-google-charts";
import { processPieChartColors } from "../SharedBaseChartFunctions";
import { IPieChartData } from "./IPieChartData";
import { processPieChartData } from "./PieChartFunctions";
import "../../chartProperty.scss";
import { pieSegmentColumnChart } from "../../../../../../Helpers/Chart/chartSegmentClickHelper";
import { GoogleHalfPieChartDefaultOptions, GooglePieChartPowerPointPrintOptions, GooglePieChartDefaultOptions } from "../../../../Constants/Chart/GoogleChartDefaultOptions";
import { ResultWithValue } from "../../../../Constants/Result/ResultWithValue";
import { RenderTabOnFocus } from "../../../../Events/RenderTabOnFocus";
import { SmallLoading } from "../../../Loading/IntermediateLoading";
import { RenderFromServiceCall } from "../RenderFromServiceCall";
import { AlertMessage } from "../../../Alerts/alertMessage";
import { MDBox } from "../../../MDBox";
import { Typography } from "@mui/material";


interface IProps {
  height?: string;
  pieSliceText?: string;
  fetchData: () => Promise<ResultWithValue<IPieChartData>>;
  onSegmentClick?: (label: string) => void;
  hash?: string;
  isHalfPieChart?: boolean;
  isPowerpointPrint?: boolean;
  hideLegend?: boolean;
  emptyMessage?: React.ReactNode;
  pieSliceFormat?: 'label' | 'value' | 'percentage';
  legendPositionRight?: boolean;
  reduceWhiteSpace?: boolean;
}

export const PieChart: React.FC<IProps> = (props: IProps) => {
  return (
    <RenderFromServiceCall
      fetchData={props.fetchData}
      key={props.hash ?? "PieChart"}
      renderFunction={(data: IPieChartData) => {
        if (data.datasets.map(d => d.value).reduce((total: number, num: number) => total + num, 0) === 0) {
          return (
            props.emptyMessage === undefined ? (
              <AlertMessage type="warning" message="No data to display" />
            ) : (
              <MDBox style={{ textAlign: 'center' }}>
                <img
                  src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/NoData.svg`}
                  alt={'No Data'}
                  style={{ maxWidth: '25%', marginTop: '10px' }}
                />
                <MDBox>
                  <Typography variant="h4" mt={2} mb={1}>
                    Oh dear!
                  </Typography>
                  <Typography variant="body2" color="secondary" component="span">
                    <p>{props.emptyMessage}</p>
                  </Typography>
                </MDBox>
              </MDBox>
            )
          );
        }
        const googleChartOptions = props.isHalfPieChart ? GoogleHalfPieChartDefaultOptions : props.isPowerpointPrint ? GooglePieChartPowerPointPrintOptions : GooglePieChartDefaultOptions;

        return (
          <RenderTabOnFocus>
            <Chart
              width="99%"
              chartType="PieChart"
              loader={<SmallLoading />}
              height={props.height ?? '500px'}
              data={processPieChartData(data, data.labels)}
              options={{
                ...googleChartOptions,
                pieSliceText: props.pieSliceFormat || 'label',
                colors: processPieChartColors(data),
                legend: props.hideLegend ? 'none' : {
                  ...googleChartOptions.legend, 
                  ...(props.legendPositionRight && { position: 'right' })
                },
                ...(props.reduceWhiteSpace && { chartArea: { 'width': '95%', 'height': '100%' } }),
              }}
              formatters={data.formatters}
              rootProps={{ 'data-testid': '3' }}
              chartEvents={
                [
                  {
                    eventName: 'select',
                    callback: pieSegmentColumnChart(
                      data.datasets,
                      props.onSegmentClick
                    ),
                  },
                ]
              }
            />
          </RenderTabOnFocus>
        );
      }
      }
    />
  )
};


